import type {
  ModePlayContext,
  ModeRegistryEntry,
  PartyGameCard,
  PartyShellAccess,
  PartyTvCastSupport,
} from "../types/api";

/** Party route/detail id → modes registry slug (catalog entry). */
export const SHELL_ID_TO_REGISTRY_SLUG: Record<string, string> = {
  imposter: "imposter",
  "imposter-online": "imposter-online",
  guess: "guess",
  act: "acting",
  who: "whoami",
  fast: "quick_questions",
  category: "category",
  challenge: "dare",
  bluff: "answer_bluff",
};

export const REGISTRY_SLUG_TO_SHELL_ID: Record<string, string> =
  Object.fromEntries(
    Object.entries(SHELL_ID_TO_REGISTRY_SLUG).map(([shell, slug]) => [
      slug,
      shell,
    ]),
  ) as Record<string, string>;

/** Party modes that support Kan-hosted online play (filter on modes grid). */
export const PARTY_ONLINE_SHELL_IDS: ReadonlySet<string> = new Set([
  "imposter-online",
  "bluff",
]);

const ORDER: string[] = [
  "imposter",
  "imposter-online",
  "guess",
  "act",
  "who",
  "fast",
  "category",
  "challenge",
  "bluff",
];

type Chrome = Omit<
  PartyGameCard,
  | "id"
  | "slug"
  | "nameAr"
  | "nameEn"
  | "access"
  | "consumesCredit"
  | "playContext"
  | "price"
  | "featured"
>;

const SHELL_CHROME: Record<string, Chrome> = {
  imposter: {
    playFormat: "solo",
    supportsTvCast: "both",
    tag: "اكتشف الجاسوس بينكم",
    desc: "كلمة سر، جواسيس، أسئلة وتصويت — الكلمة تُختار وفق الفئة.",
    tone: "mystery",
    accent: "var(--violet)",
    bg: "linear-gradient(135deg, #2A0B3A, #12081E)",
    players: "٣+",
    playersTag: "٣+ لاعبين",
    time: "١٠ د",
    how: [
      "ضيف اسامي اللاعبين و عدد الجواسيس بعدين اختار فئة عشان تكشف كلمة عشوائية للكل الا الجواسيس",
      "كل لاعب يكشف (كلمة أو جاسوس) بدون ما احد يشوف.",
      "مرحلة شرح الكلمة بدون توضيح.",
      "مرحلة أسئلة ثم تصويت؛ إذا صوتوا على الجاسوس يخمن الكلمة.",
    ],
    demo: null,
  },
  "imposter-online": {
    playFormat: "solo",
    supportsTvCast: "no",
    tag: "مو لازم قاعدين بنفس المكان",
    desc: "غرفة ، مؤقتات، وتصويت — الجميع متزامنون عبر الإنترنت.",
    tone: "mystery",
    accent: "var(--violet)",
    bg: "linear-gradient(135deg, #1E0B32, #0E0818)",
    players: "٣+",
    playersTag: "٣+ لاعبين",
    time: "أونلاين",
    how: [
      "أنشئ غرفة",
      "شارك الرمز أو الرابط؛ الضيوف يدخلون اسماً وينضمون.",
      "يبدأ المضيف عند اكتمال العدد؛ الأدوار والكلمات من الخادم.",
      "صفّ، صوّت، وأنهِ الجولة على المؤقت مع بقاء الجميع متصلين.",
    ],
    demo: null,
  },
  guess: {
    playFormat: "teams",
    supportsTvCast: "both",
    tag: "قول بس لاتقول",
    desc: "كلمة + ممنوعات؛ اشرح للفريق دون لفظها. محتوى من فئتين.",
    tone: "competitive",
    accent: "var(--hot)",
    bg: "linear-gradient(135deg, #3A0618, #140A18)",
    players: "٤+",
    playersTag: "٤+ لاعبين",
    time: "٦٠ ث/ورقة",
    how: [
      "اختر فئتين، بعدين ابدأ الجولة لتحميل الكلمات والممنوعات.",
      "لاعب من الفريق يشرح للفريق و يحاولون يصيدون الكلمة",
    ],
    demo: { word: "مطار", forbidden: ["سفر", "طائرة", "جواز سفر"] },
  },
  act: {
    playFormat: "teams",
    supportsTvCast: "both",
    tag: "مثل اللي يطلعلك",
    desc: "كلمات من فئتين؛ ٦٠ ثانية للتمثيل بين فريقين.",
    tone: "competitive",
    accent: "var(--gold)",
    bg: "linear-gradient(135deg, #2A1A06, #120A08)",
    players: "٤+",
    playersTag: "٤+ لاعبين",
    time: "٦٠ ث/دور",
    how: [
      "سمّ الفريقين واختر فئتين.",
      "لاعب من الفريق يمثل التحدي بدون كلام و الفريق يحاول يجيبها",
      "سجّل النقاط بعدين روح التحدي اللي بعده.",
    ],
    demo: null,
  },
  who: {
    playFormat: "teams",
    supportsTvCast: "both",
    tag: "حاول تكتشف كلمة الخصم",
    desc: "٦ كلمات من فئة ثلاث جولات كشف وأسئلة ونقاط.",
    tone: "mystery",
    accent: "var(--mint)",
    bg: "linear-gradient(135deg, #062A22, #081210)",
    players: "٢+",
    playersTag: "٢+ لاعبين",
    time: "٣ جولات",
    how: [
      "اختر فئة.",
      "كل فريق يكشف كلمته",
      "تسألون بعض اسألة بالدور",
      "الفريق اللي يعرف كلمة الفريق الثاني أول ياخذ النقاط",
    ],
    demo: null,
  },
  fast: {
    playFormat: "solo",
    supportsTvCast: "both",
    tag: "جاوب أول صح",
    desc: "١٢ سؤالًا من فئتين؛ كشف الإجابة ثم من أصاب أولًا.",
    tone: "competitive",
    accent: "var(--gold)",
    bg: "linear-gradient(135deg, #2A2206, #141008)",
    players: "٢+",
    playersTag: "٢+ لاعبين",
    time: "سريع",
    how: [
      "أدخل الأسماء واختر فئتين.",
      "كل اللاعبين يجاوبون",
      "بعد كشف الإجابة أول واحد جاوب صح ياخذ النقطة.",
      "يتكرر حتى انتهاء الأسئلة.",
    ],
    demo: null,
  },
  category: {
    playFormat: "solo",
    supportsTvCast: "both",
    tag: "البقاء للأقوى",
    desc: "موضوع عشوائي لكل جولة؛ بالتناوب قل كلمة لها علاقة بالموضوع.",
    tone: "competitive",
    accent: "var(--mint)",
    bg: "linear-gradient(135deg, #062A1A, #081410)",
    players: "٣+",
    playersTag: "٢+ لاعبين",
    time: "حتى يبقى واحد",
    how: [
      "أدخل اللاعبين.",
      "اختار الوقت لكل لاعب",
      "كل ما قل الوقت... اصعب",
      "يطلع موضوع عشوائي",
      "اللي دورة يقول شي له علاقة بالموضوع",
      "اللي يغلط يطلع",
      "الفايز هو الأقوى",
    ],
    demo: null,
  },
  challenge: {
    playFormat: "both",
    supportsTvCast: "both",
    tag: "تقدر على التحدي؟",
    desc: "٦ بطاقات تحدٍ عشوائية؛ فرق أو لاعبون.",
    tone: "premium",
    accent: "var(--violet)",
    bg: "linear-gradient(135deg, #1A0A2A, #100818)",
    players: "٢+",
    playersTag: "٢+ لاعبين",
    time: "٦ د",
    how: [
      "اختر فرقًا أو أسماء لاعبين.",
      "أظهر التحدي ونفّذه أو سجّل الفشل.",
      "اجمع النقاط حتى انتهاء البطاقات.",
    ],
    demo: null,
  },
  bluff: {
    playFormat: "both",
    supportsTvCast: "no",
    tag: "حاول تخدع الخصم",
    desc: "رمز غرفة ورابط للضيوف: أسئلة، إجابات مقنعة، وتصويت من الجوالات.",
    tone: "mystery",
    accent: "var(--hot)",
    bg: "linear-gradient(135deg, #3A1020, #120810)",
    players: "٣+",
    playersTag: "٢+ لاعبين",
    time: "٦ - ١٠ د",
    how: [
      "اختار فئة",
      "انسخ الرابط و ارسلة للاعبين او خلهم يمسحون الQR",
      "لما الكل يدخل حط اسمك و اضغط ابدأ",
      "حاول تخدع اللاعبين بإجابتك",
      "صوت على الإجابة الصح",
      "تاخذ نقاط اذا مصوت على الإجابة الصح او اذا احد صوت على خدعتك",
    ],
    demo: null,
  },
};

export function shellIdToRegistrySlug(shellId: string): string {
  return SHELL_ID_TO_REGISTRY_SLUG[shellId] ?? shellId;
}

/**
 * When `true`, hide the URL/QR “display target” row in setup (`PartyCastSourceToggle`).
 * Only `"yes"` suppresses it; `"both"` still shows QR + this phone, `"no"` shows it too.
 */
export function partyShellSupportsNativeTvCast(shellId: string): boolean {
  return (SHELL_CHROME[shellId]?.supportsTvCast ?? "no") === "yes";
}

export function partyShellTvCastSupport(shellId: string): PartyTvCastSupport {
  return SHELL_CHROME[shellId]?.supportsTvCast ?? "no";
}

/** Visual chrome (background, etc.) for a party shell — used by cast guest page. */
export function getPartyShellChrome(shellId: string): Chrome {
  return SHELL_CHROME[shellId] ?? SHELL_CHROME.imposter;
}

function accessFor(
  consumes_credit: boolean,
  authed: boolean,
): PartyShellAccess {
  if (!consumes_credit) return "free";
  return authed ? "owned" : "paid";
}

function buildCard(
  shellId: string,
  reg: ModeRegistryEntry,
  authed: boolean,
  featured: boolean,
): PartyGameCard {
  const chrome = SHELL_CHROME[shellId] ?? SHELL_CHROME.imposter;
  const consumes = Boolean(reg.consumes_credit);
  const playContext: ModePlayContext =
    reg.play_context === "online" ? "online" : "physical";
  return {
    id: shellId,
    slug: reg.slug,
    nameAr: reg.name_ar,
    nameEn: reg.name_en,
    access: accessFor(consumes, authed),
    consumesCredit: consumes,
    playContext,
    price: consumes ? 1 : null,
    featured,
    ...chrome,
  };
}

/** Games grid + landing: ordered cards from registry (unknown slugs skipped). */
export function registryEntriesToPartyGames(
  entries: ModeRegistryEntry[],
  authed: boolean,
): PartyGameCard[] {
  const bySlug = new Map(entries.map((e) => [e.slug, e]));
  const out: PartyGameCard[] = [];
  let featuredSet = false;
  for (const shellId of ORDER) {
    const slug = SHELL_ID_TO_REGISTRY_SLUG[shellId];
    const reg = slug ? bySlug.get(slug) : undefined;
    if (!reg) continue;
    const featured = !featuredSet && shellId === "imposter";
    if (featured) featuredSet = true;
    out.push(buildCard(shellId, reg, authed, featured));
  }
  for (const reg of entries) {
    const sid = REGISTRY_SLUG_TO_SHELL_ID[reg.slug];
    if (!sid || ORDER.includes(sid)) continue;
    out.push(buildCard(sid, reg, authed, false));
  }
  return out;
}

/** Detail page: one card by shell id; if registry missing slug, synthetic from chrome only. */
export function partyGameCardFromShell(
  shellId: string,
  entries: ModeRegistryEntry[] | undefined,
  authed = true,
): PartyGameCard {
  const slug = shellIdToRegistrySlug(shellId);
  const reg =
    entries?.find((e) => e.slug === slug) ??
    ({
      slug,
      name_en: shellId,
      name_ar: shellId,
      consumes_credit: slug !== "imposter",
      play_context: slug === "imposter-online" ? "online" : "physical",
    } as ModeRegistryEntry);
  return buildCard(
    shellId,
    reg,
    authed,
    shellId === "imposter" && reg.slug === "imposter",
  );
}
